import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';

const TrialSignup = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    companyName: '',
    name: '',
    email: '',
    googleId: '',
    googleEmail: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [emailSentMessage, setEmailSentMessage] = useState<string | null>(null);

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    try {
      // 古いトークンをクリア
      localStorage.removeItem('admin_token');
      localStorage.removeItem('company_id');
      
      // バックエンドAPI URL
      const apiUrl = import.meta.env.VITE_API_URL ||
        (window.location.hostname === 'smartmates.org' || window.location.hostname.includes('smartmates.org')
          ? 'https://sales-ai-serve-production.up.railway.app'
          : window.location.hostname.includes('railway.app')
          ? window.location.origin.replace('sales-ai-hp', 'sales-ai-serve')
          : 'http://localhost:8080');
      
      // 現在のページURLをリダイレクト先として渡す
      const currentUrl = window.location.href;
      const encodedRedirect = encodeURIComponent(currentUrl);
      
      // Google OAuth2認証ページにリダイレクト（リダイレクト先を指定）
      window.location.href = `${apiUrl}/api/auth/google/login?redirect_to=${encodedRedirect}`;
    } catch (error) {
      console.error('Error initiating Google login:', error);
      alert('Googleログインの開始に失敗しました。もう一度お試しください。');
      setIsGoogleLoading(false);
    }
  };

  // URLからGoogle認証の結果を処理（コールバック用）
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const googleEmail = urlParams.get('google_email');
    const googleId = urlParams.get('google_id');
    const token = urlParams.get('token');
    const companyId = urlParams.get('company_id');
    
    console.log('【TrialSignup】URLパラメータ:', { googleEmail, googleId, token, companyId });
    console.log('【TrialSignup】現在のURL:', window.location.href);
    
    // トークンがある場合、Google IDとメールがある場合は自動的にトライアル登録APIを呼び出す
    if (token && googleEmail && googleId) {
      console.log('【TrialSignup】トークン＋Google情報検出、自動トライアル登録を開始');
      // トークンを保存
      localStorage.setItem('admin_token', token);
      if (companyId) {
        localStorage.setItem('company_id', companyId);
      }
      
      // 自動的にトライアル登録APIを呼び出す
      const autoStartTrial = async () => {
        try {
          const region = navigator.language.startsWith('ja') ? 'jp' : 'us';
          const apiUrl = import.meta.env.VITE_API_URL ||
            (window.location.hostname === 'smartmates.org' || window.location.hostname.includes('smartmates.org')
              ? 'https://sales-ai-serve-production.up.railway.app'
              : window.location.hostname.includes('railway.app')
              ? window.location.origin.replace('sales-ai-hp', 'sales-ai-serve')
              : 'http://localhost:8080');
          
          const requestBody = {
            company_id: companyId || 'default',
            plan: 'premium',
            email: googleEmail,
            region: region,
            company_name: '', // 空でもOK（後で設定可能）
            name: '', // 空でもOK（後で設定可能）
            google_id: googleId,
            google_email: googleEmail
          };
          
          console.log('【TrialSignup】自動トライアル登録API呼び出し:', requestBody);
          
          const response = await fetch(`${apiUrl}/api/trial/create-checkout-session`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestBody),
          });
          
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
            console.error('【TrialSignup】トライアル登録APIエラー:', errorData);
            // エラー時は管理画面にリダイレクト（フォールバック）
            if (companyId) {
              window.location.href = `/c/${companyId}/admin`;
            } else {
              window.location.href = '/admin';
            }
            return;
          }
          
          const data = await response.json();
          console.log('【TrialSignup】トライアル登録API成功:', data);
          
          // admin_urlがある場合はリダイレクト、なければ管理画面にリダイレクト
          if (data.admin_url) {
            window.location.href = data.admin_url;
          } else if (companyId) {
            window.location.href = `/c/${companyId}/admin`;
          } else {
            window.location.href = '/admin';
          }
        } catch (error) {
          console.error('【TrialSignup】自動トライアル登録エラー:', error);
          // エラー時は管理画面にリダイレクト（フォールバック）
          if (companyId) {
            window.location.href = `/c/${companyId}/admin`;
          } else {
            window.location.href = '/admin';
          }
        }
      };
      
      autoStartTrial();
      return;
    }
    
    // トークンがあるがGoogle情報がない場合は従来通り管理画面にリダイレクト
    if (token) {
      console.log('【TrialSignup】トークン検出（Google情報なし）、管理画面にリダイレクト');
      localStorage.setItem('admin_token', token);
      if (companyId) {
        localStorage.setItem('company_id', companyId);
      }
      
      if (companyId) {
        console.log('【TrialSignup】管理画面にリダイレクト:', `/c/${companyId}/admin`);
        window.location.href = `/c/${companyId}/admin`;
      } else {
        console.log('【TrialSignup】管理画面にリダイレクト（デフォルト）:', '/admin');
        window.location.href = '/admin';
      }
      return;
    }
    
    if (googleEmail) {
      console.log('【TrialSignup】Googleメール検出:', googleEmail);
      setFormData(prev => ({
        ...prev,
        email: googleEmail,
        googleEmail: googleEmail,
        googleId: googleId || ''
      }));
      
      // URLパラメータをクリーンアップ
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // 地域判定（言語設定から判定）
      const region = navigator.language.startsWith('ja') ? 'jp' : 'us';
      
      // バックエンドAPI URL
      const apiUrl = import.meta.env.VITE_API_URL ||
        (window.location.hostname === 'smartmates.org' || window.location.hostname.includes('smartmates.org')
          ? 'https://sales-ai-serve-production.up.railway.app'
          : window.location.hostname.includes('railway.app')
          ? window.location.origin.replace('sales-ai-hp', 'sales-ai-serve')
          : 'http://localhost:8080');
      
      // 送信データのデバッグログ
      const requestBody = {
        company_id: 'default',  // webhookで自動生成される
        plan: 'premium',  // ビジネスプラン固定
        email: formData.email.trim(),
        region: region,  // 地域判定
        company_name: formData.companyName.trim(),  // 会社名
        name: formData.name.trim(),  // 担当者名
        google_id: formData.googleId || undefined,  // Google ID（ある場合）
        google_email: formData.googleEmail || undefined  // Google Email（ある場合）
      };
      console.log('【TrialSignup】送信データ:', requestBody);
      
      // Stripe Checkoutセッションを作成
      const response = await fetch(`${apiUrl}/api/trial/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      
      // 後払いトライアル: メール送信済みの旨を表示してから管理画面へリダイレクト（Google以外の登録）
      if (data.admin_url) {
        setEmailSentMessage(t('trialSignup.emailSentMessage'));
        setIsSubmitting(false);
        setTimeout(() => { window.location.href = data.admin_url; }, 2500);
        return;
      }
      // Stripe Checkout画面にリダイレクト
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('Checkout URL not found in response');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('エラーが発生しました。もう一度お試しください。');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* 今だけ２か月無料トライアルのバナー */}
          <div className="text-center mb-16">
            <div className="inline-flex flex-col items-center gap-3">
              <div className="bg-blue-50 border-2 border-blue-200 rounded-full px-6 py-2 mb-2">
                <span className="text-blue-600 font-bold text-sm uppercase tracking-wide">
                  <span className="line-through">{t('trialSignup.titleStrikethrough')}</span> <span>{t('trialSignup.titleNew')}</span>
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-2">
                {t('trialSignup.subtitle')}
              </h1>
              <p className="text-gray-700 text-lg md:text-xl font-semibold max-w-2xl mb-2">
                {t('trialSignup.subtitleNoteMain')}
              </p>
              <p className="text-gray-500 text-xs md:text-sm max-w-2xl">
                {t('trialSignup.subtitleNoteSmall')}
              </p>
            </div>
          </div>


          {/* フォーム */}
          <form onSubmit={handleSubmit} className="bg-gray-50 rounded-3xl p-8 md:p-12 shadow-xl">
            {emailSentMessage ? (
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 text-green-600 mb-6">
                  <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-xl md:text-2xl font-bold text-gray-900 mb-2">{emailSentMessage}</p>
                <p className="text-gray-600">{t('trialSignup.emailSentRedirecting')}</p>
              </div>
            ) : (
              <>
            <h2 className="text-3xl font-black text-gray-900 mb-8 text-center">
              {t('trialSignup.formTitle')}
            </h2>
            
            {/* Googleログインボタン */}
            <div className="mb-8">
              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={isGoogleLoading}
                className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-300 hover:border-gray-400 text-gray-800 px-6 py-4 rounded-xl text-lg font-semibold transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGoogleLoading ? (
                  <span className="flex items-center gap-3">
                    <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin"></div>
                    処理中...
                  </span>
                ) : (
                  <>
                    <svg className="w-6 h-6" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Googleアカウントで開始する
                  </>
                )}
              </button>
              <div className="text-center mt-4">
                <span className="text-gray-500 text-sm">または</span>
              </div>
            </div>
            
            <div className="space-y-6">
              <div>
                <label htmlFor="companyName" className="block text-lg font-bold text-gray-900 mb-2">
                  {t('trialSignup.companyName')} <span className="text-red-500">{t('trialSignup.required')}</span>
                </label>
                <input
                  type="text"
                  id="companyName"
                  required
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  className="w-full px-6 py-4 rounded-xl border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-lg font-semibold"
                  placeholder="株式会社○○"
                />
              </div>

              <div>
                <label htmlFor="name" className="block text-lg font-bold text-gray-900 mb-2">
                  {t('trialSignup.name')} <span className="text-red-500">{t('trialSignup.required')}</span>
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-6 py-4 rounded-xl border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-lg font-semibold"
                  placeholder="山田 太郎"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-lg font-bold text-gray-900 mb-2">
                  {t('trialSignup.email')} <span className="text-red-500">{t('trialSignup.required')}</span>
                </label>
                <input
                  type="email"
                  id="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-6 py-4 rounded-xl border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-lg font-semibold"
                  placeholder="example@company.com"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full mt-10 bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-12 py-6 rounded-2xl text-2xl font-black hover:from-blue-600 hover:to-cyan-600 transition-all hover:scale-105 shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? t('trialSignup.submitting') : t('trialSignup.submit')}
            </button>

            <p className="text-center text-gray-600 mt-6 text-sm">
              {t('trialSignup.terms')}{' '}
              <a
                href="https://smartmates.org/?page=privacy-policy"
                className="text-blue-600 hover:text-blue-800 underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                プライバシーポリシー
              </a>
            </p>
              </>
            )}
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TrialSignup;

